<template>
  <net-canvas
    ref="canvasElement"
    v-bind="$attrs"
    :mode="netMode"
    :relations="relations"
    :targets="targets"
    :resources="resources"
    :indices="indices"
    :flows="flows"
    :flow-branches="flowBranches"

    :element-values="elementValues"
    :selection="selection"

    :impulses="openRoundImpulses"
    :open-effects="unplayedOpenRoundEffects"
    :effect-values="predictableOpenEffectValues"
    :show-selected-net-item-connections="!visualizing"
    :element-costs="selectedRoundActionCosts"
    :round-index="selectedRound?.index || 0"

    :net-transform="sceneTree.netTransform"

    @select-item="selectNetItem"
    @add-select-item="addNetItemToSelection"
    @select-many="selectManyNetItems"
    @clear-all-selection="deselectAllNetItems"
    @delete="noop"
    @contextmenu="onContextMenu"
    @update:net-transform="sceneTree.updateNetTransform"
    @update:canvas-dimensions="updateCanvasDimensions"
    @update:element-value="updateElementValue"
  >
    <net-sidebar-toggle-button />
  </net-canvas>

  <scheduled-effect-create-popup
    :item="scheduledEffectMenu.item || undefined"
    :x="scheduledEffectMenu.x"
    :y="scheduledEffectMenu.y"
    @close="scheduledEffectMenu.item = null"
  />

  <action-unfulfilled-message
    v-if="unfulfilledActionPopup"
    :action-id="unfulfilledActionPopup.actionId"
    :condition-ids="unfulfilledActionPopup.conditionIds"
    :max-occurrences-reached="unfulfilledActionPopup.maxOccurrencesReached"
    @close="hideUnfulfilledActionPopup"
  />

  <scenario-end-message
    v-if="scenarioEndPopup"
    :scenario-end-id="scenarioEndPopup.scenarioEndId"
    @close="hideScenarioEndPopup"
  />
</template>

<script setup lang="ts">
import { reactive, computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { cloneDeep, without } from "lodash-es";
import { pickBy } from "@/helpers/utility/CollectionFunctions";
import { splitUid, toUid } from "@/helpers/utility/Id";
import { isInList } from "@/helpers/utility/Type";

import { calculateExpressionSafe, isExpressionRandom } from "@editor/items/expression/expressionFunctions";
import { isSceneElementNode } from "@editor/items/sceneElement/sceneElementFunctions";

import NetCanvas from "@editor/net/NetCanvas.vue";
import NetSidebarToggleButton from "@editor/net/NetSidebarToggleButton.vue";
import ScheduledEffectCreatePopup from "@editor/items/scheduledEffect/ScheduledEffectCreatePopup.vue";
import ActionUnfulfilledMessage from "@editor/items/action/ActionUnfulfilledMessage.vue";
import ScenarioEndMessage from "@editor/items/scenarioEnd/ScenarioEndMessage.vue";

import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useSimulationStore } from "@editor/items/simulation/simulations.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useFlowStore } from "@editor/items/flow/flows.store";
import { useFlowBranchStore } from "@editor/items/flow/flowBranches.store";
import { useOverlay } from "@editor/ui/overlayDialog.store";
import { useRightClickMenu } from "@core/ui/rightClickMenu.store";
import { useNetStore } from "@editor/net/net.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useIndexStore } from "@editor/items/index/indices.store";
import { useRelationStore } from "@editor/items/relation/relations.store";
import { useSceneTree } from "@editor/items/scene/sceneTree";
import { usePresenceStore } from "@editor/store/presence.store";
import { useSceneStore } from "@editor/items/scene/scenes.store";
import { useSimulatedRoundStore } from "./simulationVisualizer";

import type { Uid } from "@/helpers/utility/Id";
import type { CanvasDimensions } from "@editor/net/CanvasDimensions";
import type { CanvasSelection, ContextItemType, NetSelectableItemType } from "@editor/net/NetCanvasProps";
import { useSceneNodesStore } from "@editor/items/scene/sceneNodes.store";

const { t } = useI18n();

function noop() {}
const canvasElement = ref<InstanceType<typeof NetCanvas>>();

const scheduledEffectMenu = reactive({
  item: null as { type: "target" | "resource"; id: number } | null,
  x: 0,
  y: 0
});

const selectionStore = useSelectionStore();
const simulationStore = useSimulationStore();
const simulationViewStore = useSimulationViewStore();
const sceneTree = useSceneTree();
const resourceStore = useResourceStore();
const indexStore = useIndexStore();
const targetStore = useTargetStore();
const sceneNodeStore = useSceneNodesStore();

const scenarioEndPopup = computed(() => simulationViewStore.scenarioEndPopup);

const simulation = computed(() => simulationViewStore.simulation);
const selectedRound = computed(() => simulationViewStore.selectedRound);
const openRoundImpulses = computed(() => simulationViewStore.openRoundImpulses);
const visualizing = computed(() => simulationViewStore.visualizing);

const resources = computed(() => {
  const resourcesById = resourceStore.items;
  return pickBy(
    resourcesById,
    (e) => e != null && (simulation.value ? simulation.value.resourceIds.includes(e.id) : true)
  );
});
const relations = computed(() => useRelationStore().items);
const targets = computed(() => {
  const targetsById = targetStore.items;

  return pickBy(
    targetsById,
    (e) => e != null && (simulation.value ? simulation.value.targetIds.includes(e.id) : true) && hiddenTargetIds.value.indexOf(e.id) === -1
  );
});
const indices = computed(() => {
  const indices = indexStore.items;

  return pickBy(
    indices,
    (e) => e != null && (simulation.value ? simulation.value.indexIds.includes(e.id) : true)
  );
});

const flows = computed(() => useFlowStore().items);

const flowBranches = computed(() => useFlowBranchStore().items);

const elementValues = computed(() => simulationViewStore.elementValues);

const selection = computed((): CanvasSelection => {
  if (simulationViewStore.visualizing) {
    return {
      selected: useSimulatedRoundStore().highlights,
      highlighted: new Set(),
      active: useSimulatedRoundStore().highlights
    };
  }

  const selection = { ...selectionStore.selection };

  return selection;
});

const hiddenTargetIds = computed(() => {
  const simulation = simulationViewStore.simulation;
  if (simulation == null) return [];
  const visibleTargetIds = simulationStore.targetIds[simulation.id];
  const targetIds = useTargetStore().ids;
  const hiddenTargetIds = without(targetIds, ...visibleTargetIds);
  return hiddenTargetIds;
});

const unplayedOpenRoundEffects = computed(() => {
  if (simulationViewStore.selectedRound == null) return undefined;

  if (simulationViewStore.selectedRound.played) return undefined;

  const openEffects = simulationViewStore.getOpenRoundEffects(
    simulationViewStore.selectedRound.index
  );

  return openEffects;
});

const predictableOpenEffectValues = computed(() => {
  const effectGroups = unplayedOpenRoundEffects.value;
  if (effectGroups == null) return undefined;
  const predictedEffectValues: Record<string, number> = {};

  const parseContext = cloneDeep(simulationViewStore.parseContext);

  for (const type in effectGroups) {
    const elementEffects = effectGroups[type as keyof typeof effectGroups];
    for (const id in elementEffects) {
      const effects = elementEffects[id];
      for (const effect of effects) {
        const effectId = effect.id;
        const expression = effect.impulseExpressions[0];
        if (expression == null) continue;

        if (isExpressionRandom(expression, parseContext)) {
          continue;
        }

        predictedEffectValues[effectId] = calculateExpressionSafe(expression, parseContext).value as number;
      }
    }
  }
  return predictedEffectValues;
});

const selectedRoundActionCosts = computed(
  () => simulationViewStore.selectedRoundActionCosts
);

const unfulfilledActionPopup = computed(
  () => simulationViewStore.unfulfilledActionPopup
);

const netMode = computed(() => {
  if (!simulation.value) return "view";
  if (!simulation.value.useScheduledEffects) return "view";
  if (selectedRound.value == null) return "view";
  if (selectedRound.value.played) return "view";
  if (visualizing.value) return "view";
  if (simulation.value.complete) return "view";
  if (selectedRound.value.index < 0) return "view";
  return "influence";
});

watch(() => sceneTree.activeScene, (scene) => {
  if (scene == null) return;
  if (scene.editModeOnly) {
    console.log("active scene is editmode only, switching to default");
    const nonEditScene = useSceneStore().orderedItems.find(s => !s.editModeOnly);
    if (!nonEditScene) {
      console.warn("no non-edit scene found, staying on editmode only scene");
      return;
    }
    usePresenceStore().set("activeSceneId", nonEditScene.id);
  }
}, { immediate: true });

function selectNetItem(item: { uid: Uid<NetSelectableItemType> }) {
  if (selectionStore.contains(item)) {
    openItemInfo(item.uid);
  } else {
    selectionStore.select(item);
  }
}

function openItemInfo(uid: Uid<NetSelectableItemType>) {
  const { type, id } = splitUid(uid);
  if (type === "sceneNode") {
    const sceneNode = sceneNodeStore.byUid[toUid("sceneNode", id)];
    if (sceneNode && isSceneElementNode(sceneNode)) {
      const { type: elementType, id: elementId } = splitUid(sceneNode.data.elementUid);
      useOverlay().setItem({ type: elementType, id: elementId });
    }
    return;
  }

  useOverlay().setItem({ type: type, id: id });
}

function addNetItemToSelection(item: { uid: Uid<NetSelectableItemType> }) {
  selectionStore.add(item);
}

function selectManyNetItems(items: { uid: Uid<NetSelectableItemType> }[]) {
  selectionStore.selectMany(items);
}

function deselectAllNetItems() {
  useOverlay().hide();
  selectionStore.clearAll();
}

function onContextMenu(event: {
  item?: { uid: Uid<ContextItemType> } | null;
  mousePosition: { x: number; y: number };
  x: number;
  y: number;
}) {
  let actions = [];

  const item = event?.item;
  const itemUid = item?.uid;
  const { type: itemType, id: itemId } = itemUid ? splitUid(itemUid) : { type: null, id: null };

  if (
    itemId != null && itemType != null &&
    isInList(itemType, ["target", "resource", "index", "relation", "momentum", "flow", "flowBranch", "sceneNode"] as const)
  ) {
    actions.unshift({
      title: t("actions.showInformations"),
      callback: () => {
        openItemInfo(toUid(itemType, itemId));
      }
    });
  }

  // create impulse
  if (
    simulation.value &&
    !visualizing.value &&
    simulation.value.useScheduledEffects
  ) {
    if (item && (itemType === "sceneNode")) {
      const sceneNode = sceneNodeStore.byUid[toUid("sceneNode", itemId!)];
      if (sceneNode && isSceneElementNode(sceneNode)) {
        const { type: elementType, id: elementId } = splitUid(sceneNode.data.elementUid);
        if (elementType === "target" || elementType === "resource") {
          actions.push({
            title: t("actions.createImpulse"),
            callback: async () => {
              scheduledEffectMenu.item = { type: elementType, id: elementId };
              scheduledEffectMenu.x = event.x;
              scheduledEffectMenu.y = event.y;
            }
          });
        }
      }
    }
  }

  if (item == null) {
    actions.push({
      title: t("actions.adjustView"),
      callback: () => {
        const net = canvasElement.value;
        if (net) {
          net.adjustView();
        }
      }
    });
  }

  useRightClickMenu().show({
    actions,
    x: event.mousePosition.x,
    y: event.mousePosition.y
  });
}

function updateCanvasDimensions(dimensions: CanvasDimensions) {
  useNetStore().canvasDimensions = dimensions;
}

function hideUnfulfilledActionPopup() {
  simulationViewStore.hideUnfulfilledActionPopup();
}

function hideScenarioEndPopup() {
  simulationViewStore.hideScenarioEndPopup();
}

// ==============================
// ELEMENT VALUES

function updateElementValue({ uid, value }: { uid: Uid<"target" | "resource" | "index">; value: number }) {
  const sim = simulation.value;
  if (sim == null) return;
  if (selectedRound.value == null) return;
  if (selectedRound.value.played) return;
  const { type, id } = splitUid(uid);
  if (type == "index") return;
  const elementValue = selectedRound.value.elementValues[uid].value;
  const valueDifference = value - Number(elementValue);
  let impulseExpression = `${valueDifference}`;
  if (impulseExpression[0] !== "-") {
    impulseExpression = `+${impulseExpression}`;
  }

  const selectedRoundActionPlan = simulationStore.getActionPlan(
    sim.id,
    selectedRound.value.index
  );
  let scheduledEffect = selectedRoundActionPlan.scheduledEffects.find((effect) => {
    return effect.affectedType == type && effect.affectedId == id;
  });
  if (scheduledEffect) {
    scheduledEffect.impulseExpressions[0] = impulseExpression;
  } else {
    scheduledEffect = simulationStore.addScheduledEffect({
      simulationId: sim.id,
      roundIndex: selectedRound.value.index,
      affectedType: type,
      affectedId: id,
      impulseExpressions: [impulseExpression]
    });
  }
}
</script>

<style></style>
